/**
 * The trust page.
 *
 * Read by a procurement office, a data governance officer and an IT security
 * reviewer, often before anyone signs in and often before anyone at P3 knows
 * they exist. It is public, it needs no session, and it is printable, because
 * the first thing a vendor review does is print it.
 *
 * The structure is a trust center's, populated only with what P3 actually
 * has. The section a normal trust page does not have, "What P3 does not
 * hold", is the one that earns the rest. A page that is complete about a
 * small set of facts survives a senior review. A page that implies
 * certifications P3 does not hold does not, and P3 has already been flagged
 * once for the second behaviour.
 *
 * The W-9 and the Certificate of Insurance sit in the same list as the
 * accessibility report on purpose. Those two are often harder to extract
 * from a vendor than a compliance certificate, and having them one click away
 * removes a multi-day round trip from the buyer's own process.
 *
 * ── REBUILT ON THE TERMS PAGE'S CONTENTS RAIL, September 8 2026 ───────────
 * Thomas: "the content text is poorly presented in layout. Could we use the
 * left navigation outline from the Terms & Policies page for the Trust page?
 * Consistency."
 *
 * P3 already ships that pattern and Thomas already approved it, on
 * /app-terms-conditions: a sticky contents rail on the left, the document in
 * one readable measure to its right, and the rail marking where the reader
 * is. This page adopts it verbatim in behaviour and in structure, in this
 * surface's own tokens. Three things follow from it.
 *
 * 1. THE RAIL AND THE SECTIONS ARE ONE LIST. `SECTIONS` below drives the rail
 *    links, the section ids, the anchors and the visible eyebrow above each
 *    heading. A rail that can disagree with the document it indexes is a rail
 *    that eventually does.
 *
 * 2. THE SCROLLSPY IS A POSITION READ, NOT AN INTERSECTION TEST. The known
 *    wrong implementation watches a thin band pinned under the nav and marks
 *    the FIRST section intersecting it. That band is where the OUTGOING
 *    section's tail sits, so it runs one section behind and only ever works
 *    for sections taller than the viewport: measured on the live terms page,
 *    1 of 21 headings marked correctly. The right one, and the one here, is
 *    that the current section is the LAST one whose top has passed a reading
 *    line below the nav, which is true wherever a section starts and however
 *    tall it is. See memory `project-terms-policies-page`.
 *
 * 3. EVERY LABEL SITS ON TOP OF ITS OWN TEXT. The four column
 *    label/prose/label/prose arrangement this page used for "What P3 holds,
 *    and what P3 does not" put two lists of unequal length either side of a
 *    gutter, so no row lined up with the row across from it and the eye had
 *    to track sideways to pair a label with its sentence. Both halves are now
 *    stacked, in one measure, and every definition on the page uses the same
 *    `.eg-defs` block, so a reader learns the shape once.
 */

import { useEffect, useState, type ReactNode } from "react";

import { GatewayHero, HeroAccent } from "~/app/components/GatewayHero";
import { GatewayShell } from "~/app/components/GatewayShell";
import { Chip, SectionHead, buttonClass } from "~/app/components/primitives";
import { cn } from "~/app/lib/cn";
import { currentSectionId, type SectionTop } from "~/app/lib/contents-rail";
import { formatCount } from "~/app/lib/format";
import { LAYERS } from "~/app/lib/partner";
import {
  ABSENCES,
  DATA_RESIDENCY,
  LEGAL,
  MENTOR_SUPPLY,
  NOT_OFFERED_SHORT,
  SUBPROCESSORS,
  SUBPROCESSOR_NOTICE_DAYS,
  ROSTER_EXPLAINED,
  SUPPRESSION_EXPLAINED,
  SUPPRESSION_FLOOR,
  TRUST_DOCUMENTS,
  TRUST_UPDATES,
  WHAT_P3_DOES_NOT_HOLD,
  WHAT_P3_HOLDS,
} from "~/app/lib/pricing";

/* ==========================================================================
   The document's own table of contents

   ONE LIST, THREE JOBS: the rail's links, the sections' ids and anchors, and
   the eyebrow printed above each heading. The label is the eyebrow, verbatim,
   so the rail never names a section something the page does not call it.

   THE ORDER IS THE DOM ORDER. The scrollspy reads positions rather than
   assuming this array is sorted, so a mistake here shows up as a rail that
   scrolls oddly rather than as a wrong mark, but keep them in step anyway.
   ========================================================================== */

interface SectionMeta {
  readonly id: string;
  readonly label: string;
}

const S_DOCUMENTS: SectionMeta = { id: "documents", label: "Documents" };
/* The four layers of data, here since Sep 19 2026 (Thomas): they were
   /overview's "Your reporting" section, and a reviewer reads what the
   dashboard shows beside what P3 holds. */
const S_REPORTING: SectionMeta = { id: "reporting", label: "Your reporting" };
const S_GAPS: SectionMeta = { id: "gaps", label: "The gaps" };
const S_STUDENT_DATA: SectionMeta = { id: "student-data", label: "Student data" };
const S_AI: SectionMeta = { id: "ai-features", label: "AI features" };
const S_FLOOR: SectionMeta = { id: "privacy-floor", label: "The privacy floor" };
const S_RESIDENCY: SectionMeta = { id: "residency", label: "Where the data is" };
/* Kept at this exact id: TRUST_DOCUMENTS links the procurement pack's
   subprocessor row to "#subprocessors", and that anchor is also the one we
   have handed out in security questionnaires. */
const S_SUBPROCESSORS: SectionMeta = { id: "subprocessors", label: "Subprocessors" };
const S_ACCESS: SectionMeta = { id: "access", label: "Access control" };
const S_LOG: SectionMeta = { id: "change-log", label: "Change log" };
const S_ASK: SectionMeta = { id: "ask", label: "Ask us" };

const SECTIONS: readonly SectionMeta[] = [
  S_DOCUMENTS,
  S_REPORTING,
  S_GAPS,
  S_STUDENT_DATA,
  S_AI,
  S_FLOOR,
  S_RESIDENCY,
  S_SUBPROCESSORS,
  S_ACCESS,
  S_LOG,
  S_ASK,
];

/** Frozen at module scope so the scrollspy effect has a stable dependency. */
const SECTION_IDS: readonly string[] = SECTIONS.map((section) => section.id);

/**
 * How far below the sticky bar the reading line sits.
 *
 * It must be GREATER than the sections' own `scroll-margin-top`, which is
 * `--mast-nav-h + --sp-6` (24px). If it were smaller, clicking a rail link
 * would land the section top just ABOVE the line and the rail would mark the
 * section before it, which is the same off-by-one the intersection version
 * had, arrived at from the other direction.
 */
const READING_AIR = 48;

/** The bar's height, from the same custom property the CSS scrolls to. */
function measureReadingLine(): number {
  if (typeof window === "undefined") return 76 + READING_AIR;
  const page = document.querySelector(".eg-page");
  if (!(page instanceof HTMLElement)) return 76 + READING_AIR;
  const raw = window
    .getComputedStyle(page)
    .getPropertyValue("--mast-nav-h")
    .trim();
  const navHeight = Number.parseFloat(raw);
  return (Number.isFinite(navHeight) ? navHeight : 76) + READING_AIR;
}

/**
 * The current section: the LAST one whose top has passed the reading line.
 *
 * Passive and rAF-throttled, because a scroll listener that reads layout on
 * every event runs several hundred times a second on a trackpad and this
 * answer changes about ten times in a full page.
 */
function useContentsRail(ids: readonly string[]): string {
  const [current, setCurrent] = useState<string>(ids[0] ?? "");

  useEffect(() => {
    if (ids.length === 0) return;
    let frame = 0;
    let line = measureReadingLine();

    const read = () => {
      frame = 0;
      const tops: SectionTop[] = [];
      for (const id of ids) {
        const element = document.getElementById(id);
        if (element) tops.push({ id, top: element.getBoundingClientRect().top });
      }
      const doc = document.documentElement;
      const atBottom =
        window.innerHeight + window.scrollY >= doc.scrollHeight - 2;
      const next = currentSectionId(tops, line, atBottom);
      setCurrent((was) => (was === next || next === "" ? was : next));
    };

    const request = () => {
      if (frame === 0) frame = window.requestAnimationFrame(read);
    };
    const onResize = () => {
      line = measureReadingLine();
      request();
    };

    read();
    window.addEventListener("scroll", request, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("scroll", request);
      window.removeEventListener("resize", onResize);
      if (frame !== 0) window.cancelAnimationFrame(frame);
    };
  }, [ids]);

  return current;
}

/* ==========================================================================
   The rail

   ONE LANDMARK, not two. It is a `nav` labelled by its own visible title,
   rather than an `aside` wrapping a `nav`, which announces the same words
   twice to a screen reader user moving between landmarks.

   `aria-current="location"` and not "page": every one of these links stays on
   this page, and "page" would tell assistive technology the reader had
   navigated somewhere they have not.
   ========================================================================== */

function ContentsRail({ current }: { current: string }) {
  return (
    <nav className="eg-toc no-print" aria-labelledby="trust-toc-title">
      <div className="eg-toc-in">
        <p className="eg-toc-title" id="trust-toc-title">
          Contents
        </p>
        <div className="eg-toc-list">
          {SECTIONS.map((section) => (
            <a
              className="eg-toc-link"
              key={section.id}
              href={`#${section.id}`}
              aria-current={current === section.id ? "location" : undefined}
            >
              {section.label}
            </a>
          ))}
        </div>
      </div>
    </nav>
  );
}

/* ==========================================================================
   One section, wired to one rail entry
   ========================================================================== */

interface TrustSectionProps {
  meta: SectionMeta;
  title: ReactNode;
  lede?: ReactNode;
  children: ReactNode;
}

function TrustSection({ meta, title, lede, children }: TrustSectionProps) {
  return (
    <section className="eg-sec" id={meta.id} aria-labelledby={`${meta.id}-head`}>
      <SectionHead
        id={`${meta.id}-head`}
        eyebrow={meta.label}
        title={title}
        lede={lede}
      />
      {children}
    </section>
  );
}

/**
 * A label above its own body, on a hairline. The page's only definition
 * shape, used by the holdings, the AI gate and the sign-in controls, so a
 * reviewer learns to read it once and never tracks across a gutter.
 *
 * `tone="absent"` draws the struck accent mark the ledger of absences uses,
 * which is how the "does not hold" half is told apart from the "holds" half
 * now that the two are stacked rather than set side by side.
 */
function Defs({
  rows,
  tone = "present",
}: {
  rows: readonly { readonly label: string; readonly body: ReactNode }[];
  tone?: "present" | "absent";
}) {
  return (
    <dl className={tone === "absent" ? "eg-defs is-absent" : "eg-defs"}>
      {rows.map((row) => (
        <div className="eg-def" key={row.label}>
          <dt>{row.label}</dt>
          <dd>{row.body}</dd>
        </div>
      ))}
    </dl>
  );
}

export interface TrustProps {
  chrome?: boolean;
  className?: string;
}

export function Trust({ chrome = true, className }: TrustProps) {
  const current = useContentsRail(SECTION_IDS);

  return (
    <GatewayShell
      current="trust"
      chrome={chrome}
      mainId="trust-main"
      skipLabel="Skip to the documents"
      className={className}
    >
      {/* ---------------------------------------------------------------
          The masthead. Identical surface to the pricing page: a reviewer
          who arrives here from a link in a security questionnaire, having
          never seen the gateway, should still be looking at P3's own
          chrome rather than at a white document.
          --------------------------------------------------------------- */}
      <section className="eg-hero">
        <GatewayHero
          eyebrow="Security"
          title={
            <>
              Everything your review will ask for,{" "}
              <HeroAccent>before you ask</HeroAccent>.
            </>
          }
          sub="The documents, the subprocessors, the data handling, the age gate on the AI features, and what P3 does not have."
        />
      </section>

      <div className="eg-trust-body">
        <ContentsRail current={current} />

        <div className="eg-trust-doc">
          {/* -------------------------------------------------------------
              Documents
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_DOCUMENTS}
            title="The procurement pack"
            lede="Published means it is on this site now. On request means we send it by email, we just do not publish it openly. In preparation means it does not exist yet, and we say so rather than link to nothing."
          >
            <div className="eg-docs">
              {TRUST_DOCUMENTS.map((doc) => (
                <div className="eg-doc" key={doc.name}>
                  <div>
                    <p className="eg-doc-name">{doc.name}</p>
                    <p className="eg-doc-detail">{doc.detail}</p>
                  </div>
                  <p className="eg-doc-action">
                    {doc.status === "published" && doc.href ? (
                      <a className="e-print-url" href={doc.href}>
                        {doc.href.startsWith("#") ? "Read below" : "Download"}
                      </a>
                    ) : (
                      <Chip tone="neutral">{doc.status === "in-preparation" ? "In preparation" : "On request"}</Chip>
                    )}
                  </p>
                </div>
              ))}
            </div>
            <p className="e-meta eg-prose">
              Every published version of the Enterprise Subscription Terms and
              the Data Sharing Addendum stays at its own permanent address and
              is never edited after publication, so you can always prove what
              your institution accepted and on what date.
            </p>
          </TrustSection>

          {/* -------------------------------------------------------------
              Your reporting: the four layers of data (Sep 19 2026, moved
              from /overview, Thomas). The same 2x2 as it was there: an
              accent rule on top, a faded index, four checks, and the fourth
              card on the dark gradient, whose pill says the outcomes are
              student reported. What P3 holds to produce them is the Student
              data section below.
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_REPORTING}
            title="Four layers of data on your students"
            lede="What P3 observes on the platform, what students report themselves, and the line between the two."
          >
            <div className="eg-dl-grid">
              {LAYERS.map((layer) => (
                <article className={cn("eg-dl-card", layer.moat && "is-moat")} key={layer.index}>
                  {layer.pill ? <span className="eg-dl-pill">{layer.pill}</span> : null}
                  <p className="eg-dl-num" aria-hidden="true">
                    {layer.index}
                  </p>
                  <h3>{layer.title}</h3>
                  <p>{layer.detail}</p>
                  <ul className="eg-ticks eg-dl-feats">
                    {layer.items.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                </article>
              ))}
            </div>
          </TrustSection>

          {/* -------------------------------------------------------------
              WHAT P3 DOES NOT DO.

              This section came off the pricing page at Thomas's instruction:
              "move the what we do not do content out of the pricing page and
              onto /trust". It arrived there as a tabbed list, which is what a
              list looks like when it has been dumped rather than designed.

              FIRST, all thirteen absences as a scannable ledger, one to a
              row on a hairline with a struck mark. It ran in two columns
              while the page was full width; in the document column it is one,
              because at this measure every item fits on a single line and a
              single column of one-line rows is the most scannable thing this
              page can be.

              SECOND, the five that a security questionnaire actually stops
              on, written out in full underneath. The short line tells the
              reviewer whether they can proceed; the block tells them why, and
              what the upgrade path is where there is one.

              It sits near the top, after the documents and the four layers
              the dashboard reports, rather than at the bottom. A trust page
              that buries its gaps is a trust page that has been caught out
              once.
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_GAPS}
            title="What P3 does not do"
            lede="We would rather you read this here than discover it in a security review, because discovering it there costs you a cycle and costs us the deal."
          >
            <ul className="eg-nots">
              {NOT_OFFERED_SHORT.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <h3 className="e-h3 eg-nots-more">Five of those, in full</h3>
            <div className="eg-absences">
              {ABSENCES.map((item) => (
                <section
                  className="eg-absence"
                  key={item.title}
                  aria-labelledby={`absence-${slug(item.title)}`}
                >
                  {/* h4, not h3: "Five of those, in full" is the h3 that
                      introduces this group, and five siblings of their own
                      introduction is a heading outline that tells a screen
                      reader user the wrong thing about the structure. */}
                  <h4 id={`absence-${slug(item.title)}`} className="e-h3">
                    {item.title}
                  </h4>
                  <p>{item.body}</p>
                </section>
              ))}
            </div>
            <p className="eg-prose eg-note">
              P3 also does not guarantee that any student is matched with a
              mentor. {formatCount(MENTOR_SUPPLY.approvedMentors)} approved
              mentors serve every institution on P3, and that number is
              published on the pricing page and inside the dashboard rather
              than kept in a sales conversation.
            </p>
          </TrustSection>

          {/* -------------------------------------------------------------
              Student data.

              THE FOUR COLUMN TABLE IS GONE. Two lists of unequal length
              either side of a gutter meant no row lined up with the row
              across from it, and pairing a label to its sentence took a
              sideways read. Both halves are stacked now, in one measure,
              each label directly above its own text, and the half that is
              a list of absences carries the struck accent mark the ledger
              above already taught the reader.
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_STUDENT_DATA}
            title="What P3 holds, and what P3 does not"
            lede="Students register with P3 directly and consent to P3 directly. P3 is the controller of that data, your institution receives aggregated reporting rather than student records, and P3 is not a school official under FERPA."
          >
            <div className="eg-holds">
              <h3 className="e-h3 eg-holds-h">What P3 holds</h3>
              <Defs rows={WHAT_P3_HOLDS} />
            </div>
            <div className="eg-holds">
              <h3 className="e-h3 eg-holds-h">What P3 does not hold</h3>
              <Defs rows={WHAT_P3_DOES_NOT_HOLD} tone="absent" />
            </div>
          </TrustSection>

          {/* -------------------------------------------------------------
              THE AGE GATE ON THE AI FEATURES.

              Added when the gateway was repositioned to sell the whole
              student suite rather than mentorship alone (September 8, 2026).
              The moment the pricing page names Pulse AI and CV review as
              things a student gets, a reviewer is entitled to know who can
              actually reach them, and to know it here rather than in month
              three. The gate is 18+, it is stated on the pricing card as
              well, and it is enforced twice: the route returns before the
              chat mounts, and the proxy refuses the call.

              Every sentence below was read out of p3-web rather than
              described from memory: features/coach/coach.route.tsx and
              features/career/career.route.tsx both branch on `isAdult`, and
              features/coach/lib/resource-links.ts is the allowlist.
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_AI}
            title="Who can use Pulse AI, and what it can reach"
            lede="Pulse AI and the CV review send student text to Anthropic, so they are limited to students aged 18 and older. A younger student cannot reach them and no call is made for them."
          >
            <Defs
              rows={[
                {
                  label: "Ages 18 and older",
                  body: "The gate is applied where the screen renders and again where the request is made, so a stale link or a saved bookmark reaches an explanation rather than a conversation. Everything else in P3, including opportunities, career pathways, the open question pool and mentor video answers, is open at every age.",
                },
                {
                  label: "Anthropic processes the message",
                  body: `Anthropic, PBC is named in the subprocessor list below, with its region, and it is covered by the same ${SUBPROCESSOR_NOTICE_DAYS} days notice as every other vendor on that list.`,
                },
                {
                  label: "It links to a fixed list, not to the open web",
                  body: "Pulse answers may only link to URLs on a curated list P3 maintains, which is how a student gets a working link to federal student aid rather than a plausible address the model invented.",
                },
                {
                  label: "It cannot see your dashboard",
                  body: "Pulse runs on the student side of P3, against that student's own profile and history. It has no access to institutional reporting, and no prompt from a student can return your figures.",
                },
              ]}
            />
          </TrustSection>

          {/* -------------------------------------------------------------
              The suppression commitment, in plain language
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_FLOOR}
            title={`Nothing smaller than ${SUPPRESSION_FLOOR} students is ever shown`}
            lede="This is the commitment your data governance office will care about most, so it is written here in the words we would use out loud."
          >
            <div className="eg-prose">
              {SUPPRESSION_EXPLAINED.map((paragraph) => (
                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
              ))}
              {ROSTER_EXPLAINED.map((paragraph) => (
                <p key={paragraph.slice(0, 32)}>{paragraph}</p>
              ))}
            </div>
            <div className="eg-absences">
              <section className="eg-absence" aria-labelledby="isolation">
                <h3 id="isolation" className="e-h3">
                  One institution cannot see another
                </h3>
                <p>
                  No endpoint that returns tenant analytics, membership or
                  billing data accepts an organization identifier. The
                  organization is read from a server-side session row, so a
                  request cannot ask for someone else's data by changing a
                  parameter. That removes the whole class of problem as a
                  property of the design rather than as a control we have to
                  defend.
                </p>
              </section>
              <section className="eg-absence" aria-labelledby="no-student-export">
                <h3 id="no-student-export" className="e-h3">
                  There is no student level export
                </h3>
                <p>
                  Not for an administrator, not for an owner, not on request,
                  and not for a fee. Offering one would defeat the suppression
                  commitment, so it does not exist. You get your own aggregates
                  as CSV and as a print ready briefing, both generated from the
                  same stored figures the dashboard renders, so the page and the
                  export cannot disagree.
                </p>
              </section>
              <section className="eg-absence" aria-labelledby="affiliation">
                <h3 id="affiliation" className="e-h3">
                  Affiliation is asserted by the student
                </h3>
                <p>
                  A student adds your institution themselves. From your join
                  link or code, they see exactly what you will and will not be
                  able to see before anything is written, and they can remove
                  it at any time. A student can also pick your institution in
                  the P3 app&rsquo;s profile, where the app asks them only to
                  confirm the change. P3 does not verify that a student who
                  claims your institution attends it, and has no roster feed
                  that could.
                </p>
              </section>
              <section className="eg-absence" aria-labelledby="no-roster">
                <h3 id="no-roster" className="e-h3">
                  P3 does not accept student rosters
                </h3>
                <p>
                  P3 does not take a roster of your students in any form,
                  including a sync with your student information system. Our
                  Data Sharing Addendum deliberately does not claim school
                  official status under 34 CFR 99.31(a)(1). Students add your
                  institution themselves, and the way to reach them at scale is
                  an advisor send: a message your own staff send, from a person
                  the student already trusts.
                </p>
              </section>
            </div>
          </TrustSection>

          {/* -------------------------------------------------------------
              Data residency
              ------------------------------------------------------------- */}
          <TrustSection meta={S_RESIDENCY} title="Data residency">
            <div className="eg-prose">
              <p>{DATA_RESIDENCY}</p>
              <p>
                We state it that way because it is accurate. A flat claim that
                nothing ever leaves the United States would be easier to read
                and would be wrong, since edge compute and content delivery run
                at points of presence worldwide by default.
              </p>
            </div>
          </TrustSection>

          {/* -------------------------------------------------------------
              Subprocessors.

              TWO COLUMNS, NOT THREE. The region used to sit in a third
              column pinned to the right edge, which put a vendor's name and
              its region at opposite ends of a wide row with a paragraph
              between them. It is a property of the vendor, so it is printed
              under the vendor.
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_SUBPROCESSORS}
            title="Every vendor that processes personal data"
            lede={`We give your administrators at least ${SUBPROCESSOR_NOTICE_DAYS} days notice by email before adding one. If you object on documented data protection grounds within that period we discuss it in good faith, and if we cannot resolve it you may terminate.`}
          >
            <div className="eg-vendors">
              {SUBPROCESSORS.map((sub) => (
                <div className="eg-vendor" key={sub.name}>
                  <div>
                    <p className="eg-vendor-name">{sub.name}</p>
                    <p className="eg-vendor-region">{sub.region}</p>
                  </div>
                  <p className="eg-vendor-purpose">{sub.purpose}</p>
                </div>
              ))}
            </div>
            <p className="e-meta eg-prose">
              Stripe appears on this list because it processes your
              institution's billing contact and payment details. It never
              receives student data.
            </p>
          </TrustSection>

          {/* -------------------------------------------------------------
              Sign-in and access
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_ACCESS}
            title="How your administrators sign in"
            lede="There is no password to leak and no single sign-on to configure, which is a tradeoff rather than a feature, and here is exactly what it means."
          >
            <Defs
              rows={[
                {
                  label: "Six digit single-use codes",
                  body: "A code, not a clickable link, because university mail security often follows links automatically and would spend a magic link before your administrator did. Codes are single use, expire in ten minutes, allow five attempts and are stored salted and hashed, so reading the database yields nothing usable.",
                },
                {
                  label: "Your own domains only",
                  body: "An invitation can only be sent to an address at an email domain your institution holds here: one someone active on your team already uses, or one P3 added because you asked. Adding one is a person at P3 acting on your request rather than a DNS check, and it is written into your own audit trail with who did it and when. There is no way to invite an arbitrary address into your tenant.",
                },
                {
                  label: "Sessions you can end",
                  body: "A session is an opaque random identifier in a same-origin, HttpOnly cookie, backed by a row P3 can revoke. Removing an administrator revokes their sessions in the same transaction, so access ends on their next request. A stateless signed token cannot answer that question honestly and we did not use one.",
                },
                {
                  label: "Three roles",
                  body: "Owner (billing, members and data), admin (members and data) and viewer (data only). Roles are enforced on the server by re-reading the membership row, never by hiding a button.",
                },
                {
                  label: "An audit trail you can export",
                  body: "Every sign-in, member invitation, role change, member removal, metrics read and export is written to an append-only log with the actor, the organization, the timestamp and the source address. It is your log, and an owner or admin downloads it as a CSV from Settings. A wrong code is logged too, for P3's own security monitoring, because until a code is right it names no institution.",
                },
              ]}
            />
          </TrustSection>

          {/* -------------------------------------------------------------
              Change log
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_LOG}
            title="What changed on this page, and when"
            lede="Dated, so a reviewer can tell whether they are reading something current."
          >
            <div className="eg-log">
              {TRUST_UPDATES.map((update) => (
                <div className="eg-log-row" key={update.body.slice(0, 40)}>
                  <p className="eg-log-date">{update.date}</p>
                  <p className="eg-log-body">{update.body}</p>
                </div>
              ))}
            </div>
          </TrustSection>

          {/* -------------------------------------------------------------
              Contact
              ------------------------------------------------------------- */}
          <TrustSection
            meta={S_ASK}
            title="Something here is not enough for your process"
            lede="Tell us which control, which criterion or which document, and we will answer it or tell you plainly that we cannot."
          >
            <div className="eg-cta">
              <a
                className={buttonClass("primary")}
                href={`mailto:${LEGAL.contactEmail}?subject=Security%20review%20question`}
              >
                Email {LEGAL.contactEmail}
              </a>
              <a className={buttonClass("secondary")} href="/pricing">
                Back to pricing
              </a>
            </div>
            <p className="eg-cta-line">
              If your process requires SOC 2 today, say so at the start rather
              than at contract stage. We would rather lose a cycle than waste
              yours.
            </p>
          </TrustSection>
        </div>
      </div>
    </GatewayShell>
  );
}

function slug(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}
